import React, { useState, useMemo, useEffect } from 'react'
import Card from '../../components/Card.jsx'
import * as api from '../../services/api.js'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'
import { todayISO } from '../../utils/dateFormat.js'

const BUILT_IN_TIPS = [
  {
    category: 'Cleaning & Prep',
    tips: [
      { title: 'Wash strawberries with vinegar', body: 'Soak strawberries in 1 part white vinegar to 3 parts water for 5 minutes, then rinse thoroughly. This removes bacteria, pesticide residue, and mould spores — they\'ll stay fresh in the fridge for much longer.' },
      { title: 'Dry your proteins before searing', body: 'Pat meat, fish, or tofu completely dry with paper towels before adding to a hot pan. Moisture is the enemy of a good sear — steam prevents the caramelisation you\'re after.' },
      { title: 'Peel ginger with a spoon', body: 'The skin is thin and follows the curves. A spoon removes it cleanly with almost no waste, far easier than a peeler or knife.' },
      { title: 'Revive wilted herbs', body: 'Trim the stems and place in ice-cold water for 15–30 minutes. Most herbs (parsley, coriander, mint) will crisp back up.' },
    ],
  },
  {
    category: 'Mise en Place',
    tips: [
      { title: 'Prep everything before you start', body: 'Chop, measure, and set out all your ingredients before turning on the heat. Cooking flows better and you\'re far less likely to burn something while hunting for a missing ingredient.' },
      { title: 'Pre-measure your spices', body: 'Tip measured spices into small bowls before cooking. When things heat up quickly, you can add them in one smooth motion without scrambling.' },
      { title: 'Read the full recipe first', body: 'Before you touch anything, read the entire recipe. Nothing is worse than discovering a "refrigerate overnight" step when you\'re already halfway through.' },
    ],
  },
  {
    category: 'Flavour & Seasoning',
    tips: [
      { title: 'Acid brightens everything', body: 'A squeeze of lemon or a splash of vinegar at the end of cooking lifts and brightens dull flavours. Try it on soups, pasta, roasted vegetables — nearly anything.' },
      { title: 'Taste as you go', body: 'Season at every stage, not just at the end. Building flavour gradually is the difference between "fine" and "wow".' },
      { title: 'Salt pasta water generously', body: 'It should taste like mild seawater. This is the only chance you get to season the pasta itself — not just the sauce on top.' },
      { title: 'Bloom your spices', body: 'Toast whole spices in a dry pan, or fry ground spices in oil for 30–60 seconds before adding other ingredients. This releases fat-soluble flavour compounds that water-based cooking misses.' },
      { title: 'Salt meat at the right time', body: 'Salt at least 40 minutes before cooking, or right before. Between 3–39 minutes, the salt draws out moisture that doesn\'t fully re-absorb — the worst window. Salting early lets it pull back in, seasoning deeper into the meat.' },
    ],
  },
  {
    category: 'Heat & Cooking',
    tips: [
      { title: 'Preheat your pan properly', body: 'A cold pan leads to sticking and steaming instead of searing. Heat the pan first, then add oil, then add food. A drop of water that skitters across the surface means it\'s ready.' },
      { title: 'Don\'t crowd the pan', body: 'Too many ingredients at once drops the temperature, causing steaming instead of browning. Cook in batches if needed — worth the extra few minutes.' },
      { title: 'Let meat rest after cooking', body: 'Rest for roughly half the cooking time (at least 5 minutes for a steak, 15–20 for a roast). The juices redistribute and you\'ll lose far less when you cut.' },
      { title: 'Use the right heat level', body: 'High heat for searing and stir-frying, medium for sautéing and eggs, low for simmering and melting. Most dishes are ruined by cooking too hot, not too slow.' },
    ],
  },
  {
    category: 'Baking',
    tips: [
      { title: 'Measure by weight, not volume', body: 'A cup of flour can vary by 20–30% depending on how it\'s scooped. A kitchen scale gives consistent results every time — especially important for bread and pastry.' },
      { title: 'Room temperature ingredients matter', body: 'Cold butter or eggs can cause batters to seize or not emulsify properly. Take dairy and eggs out 30–60 minutes before baking.' },
      { title: 'Don\'t overmix after adding flour', body: 'Overmixing develops gluten and makes cakes and muffins tough and chewy. Stir until just combined — a few streaks of flour are absolutely fine.' },
      { title: 'Oven temperature is rarely accurate', body: 'Most ovens run hot or cold by 10–25°C. An oven thermometer costs very little and will transform your baking results.' },
    ],
  },
  {
    category: 'Storage & Freshness',
    tips: [
      { title: 'Store soft herbs like flowers', body: 'Trim the stems and stand parsley, coriander, or basil in a glass of water in the fridge. Covered loosely, they\'ll last 1–2 weeks instead of a few days.' },
      { title: 'Keep avocados from browning', body: 'Press cling film directly onto the cut surface of avocado — no air gap. A squeeze of lemon or lime also helps considerably.' },
      { title: 'Freeze leftover tomato paste', body: 'Portion tablespoon-sized blobs onto parchment, freeze until solid, then bag them. No more wasted half-tins.' },
      { title: 'Freeze overripe bananas', body: 'Peel and freeze them in a bag. Perfect for banana bread, smoothies, or nice cream whenever you need them.' },
      { title: 'Store onions and potatoes separately', body: 'Onions emit gases that cause potatoes to sprout faster. Store them in different cool, dark places.' },
    ],
  },
  {
    category: 'Kitchen Hacks',
    tips: [
      { title: 'Use a damp cloth under the chopping board', body: 'A wet cloth or paper towel under your board stops it from sliding — safer and much less frustrating.' },
      { title: 'Chill your bowl for whipping cream', body: 'A cold bowl and cold cream whips faster and holds its shape better. Pop the bowl in the freezer for 10 minutes first.' },
      { title: 'A potato masher crushes more than potatoes', body: 'Use it to break up minced meat in a pan, crush beans for a quick mash, or make chunky guacamole.' },
      { title: 'Microplane for everything', body: 'A microplane grater is one of the most useful kitchen tools — for zesting citrus, grating garlic, shaving parmesan, or grating frozen butter into pastry.' },
    ],
  },
]

const ALL_CATEGORIES = BUILT_IN_TIPS.map(c => c.category)
const TIPS_PER_CAT = 3

// ── User note card ────────────────────────────────────────────────────────────

function UserNoteCard({ category, note, onUpdate, onDelete }) {
  const [editing,     setEditing]     = useState(false)
  const [editDraft,   setEditDraft]   = useState({ title: note.title, body: note.body || '' })
  const [showNote,    setShowNote]    = useState(false)
  const [noteText,    setNoteText]    = useState(note.user_note || '')
  const [confirmDel,  setConfirmDel]  = useState(false)

  function saveEdit() {
    if (!editDraft.title.trim()) return
    onUpdate(category, note.id, { ...editDraft })
    setEditing(false)
  }

  function handleNoteBlur() {
    if (noteText !== (note.user_note || '')) {
      onUpdate(category, note.id, { user_note: noteText })
    }
  }

  if (editing) {
    return (
      <div className="tip-accordion" style={{ borderLeft: '2px solid var(--accent)' }}>
        <div className="tip-card-edit-area">
          <input
            className="field-input"
            value={editDraft.title}
            onChange={e => setEditDraft(d => ({ ...d, title: e.target.value }))}
            placeholder="Tip title"
            autoFocus
          />
          <textarea
            className="field-textarea"
            rows={3}
            value={editDraft.body}
            onChange={e => setEditDraft(d => ({ ...d, body: e.target.value }))}
            placeholder="What did you learn?"
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button className={`save-btn ${editDraft.title.trim() ? 'ready' : ''}`} onClick={saveEdit}>Save</button>
            <button className="ghost-btn" onClick={() => { setEditing(false); setEditDraft({ title: note.title, body: note.body || '' }) }}>Cancel</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="tip-accordion" style={{ borderLeft: '2px solid var(--accent)' }}>
      <div className="tip-accordion-header">
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: '0.65rem', background: 'var(--accent)', color: 'white', borderRadius: 20, padding: '1px 7px', flexShrink: 0 }}>
            mine
          </span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{note.title}</span>
        </span>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button className="tip-action-btn" onClick={() => setEditing(true)} title="Edit">✏️</button>
          <button className="tip-action-btn tip-action-btn--danger" onClick={() => setConfirmDel(true)} title="Delete">✕</button>
        </div>
      </div>

      {note.body && (
        <p className="tip-accordion-body" style={{ paddingBottom: 6 }}>{note.body}</p>
      )}

      {note.user_note && !showNote && (
        <p className="tip-note-preview">📝 {note.user_note}</p>
      )}

      {showNote && (
        <textarea
          className="tip-note-textarea"
          value={noteText}
          onChange={e => setNoteText(e.target.value)}
          onBlur={handleNoteBlur}
          placeholder="Add your personal notes or experience…"
          rows={2}
        />
      )}

      <div className="tip-card-actions">
        <button
          className="tip-action-btn"
          onClick={() => setShowNote(n => !n)}
        >
          {showNote ? 'Hide note' : note.user_note ? '📝 Note' : '+ Note'}
        </button>
      </div>

      <ConfirmDialog
        open={confirmDel}
        title="Delete this tip?"
        message="Your personal tip will be permanently removed."
        confirmLabel="Delete"
        onConfirm={() => { setConfirmDel(false); onDelete(category, note.id) }}
        onCancel={() => setConfirmDel(false)}
      />
    </div>
  )
}

// ── Add note form ─────────────────────────────────────────────────────────────

function AddNoteForm({ category, onSave }) {
  const [open,  setOpen]  = useState(false)
  const [draft, setDraft] = useState({ title: '', body: '' })

  function save() {
    if (!draft.title.trim()) return
    onSave(category, draft)
    setDraft({ title: '', body: '' })
    setOpen(false)
  }

  if (!open) {
    return (
      <button className="ghost-btn" style={{ marginTop: 10, fontSize: '0.75rem' }} onClick={() => setOpen(true)}>
        + Add my note
      </button>
    )
  }

  return (
    <div style={{ marginTop: 10, background: 'rgba(255,255,255,0.45)', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <input
        className="field-input"
        value={draft.title}
        onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
        placeholder="Tip title"
        autoFocus
      />
      <textarea
        className="field-textarea"
        rows={2}
        value={draft.body}
        onChange={e => setDraft(d => ({ ...d, body: e.target.value }))}
        placeholder="What did you learn? (optional)"
      />
      <div style={{ display: 'flex', gap: 6 }}>
        <button className={`save-btn ${draft.title.trim() ? 'ready' : ''}`} onClick={save}>Save</button>
        <button className="ghost-btn" onClick={() => { setOpen(false); setDraft({ title: '', body: '' }) }}>Cancel</button>
      </div>
    </div>
  )
}

// ── Cooking note card ─────────────────────────────────────────────────────────

function CookingNoteCard({ note, onDelete }) {
  const [confirmDel, setConfirmDel] = useState(false)

  return (
    <div className="cooking-note-item">
      {note.title && <div className="cooking-note-item__title">{note.title}</div>}
      <p className="cooking-note-item__body">{note.body}</p>
      <div className="cooking-note-item__actions">
        <button
          className="tip-action-btn tip-action-btn--danger"
          onClick={() => setConfirmDel(true)}
        >
          Delete
        </button>
      </div>
      <ConfirmDialog
        open={confirmDel}
        title="Delete this note?"
        message="Your cooking note will be permanently removed."
        confirmLabel="Delete"
        onConfirm={() => { setConfirmDel(false); onDelete(note.id) }}
        onCancel={() => setConfirmDel(false)}
      />
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function CookingTips() {
  const [notes,         setNotes]         = useState({})
  const [cookingNotes,  setCookingNotes]  = useState([])
  const [openTip,       setOpenTip]       = useState(null)
  const [search,        setSearch]        = useState('')
  const [activeCategories, setActiveCategories] = useState([])
  const [myNotesOnly,   setMyNotesOnly]   = useState(false)
  const [expandedCats,  setExpandedCats]  = useState({})
  const [addingCookNote, setAddingCookNote] = useState(false)
  const [cookNoteDraft,  setCookNoteDraft]  = useState({ title: '', body: '' })

  useEffect(() => {
    api.getCookingTips().then(d => setNotes(d || {}))
    api.getCookingNotes().then(d => setCookingNotes(d || []))
  }, [])

  // ── User notes (per category blob) ─────────────────────────────────────────

  function updateNotes(updater) {
    setNotes(prev => {
      const next = updater(prev)
      api.saveCookingTips(next)
      return next
    })
  }

  function addNote(category, draft) {
    updateNotes(prev => ({
      ...prev,
      [category]: [{ id: Date.now(), ...draft }, ...(prev[category] || [])],
    }))
  }

  function updateNote(category, id, updates) {
    updateNotes(prev => ({
      ...prev,
      [category]: (prev[category] || []).map(n => n.id === id ? { ...n, ...updates } : n),
    }))
  }

  function deleteNote(category, id) {
    updateNotes(prev => ({
      ...prev,
      [category]: (prev[category] || []).filter(n => n.id !== id),
    }))
  }

  // ── Cooking notes (global) ──────────────────────────────────────────────────

  async function addCookingNote() {
    const body = cookNoteDraft.body.trim()
    if (!body) return
    const note = { title: cookNoteDraft.title.trim() || null, body }
    const result = await api.saveCookingNote(note)
    const withId = { ...note, id: result?.id || Date.now(), created_at: new Date().toISOString() }
    setCookingNotes(prev => [withId, ...prev])
    setCookNoteDraft({ title: '', body: '' })
    setAddingCookNote(false)
  }

  async function deleteCookingNote(id) {
    setCookingNotes(prev => prev.filter(n => n.id !== id))
    await api.deleteCookingNote(id)
  }

  // ── Category filter ─────────────────────────────────────────────────────────

  function toggleCategory(cat) {
    setActiveCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    )
  }

  function clearFilters() {
    setSearch('')
    setActiveCategories([])
    setMyNotesOnly(false)
  }

  const hasFilters = search.trim() || activeCategories.length > 0 || myNotesOnly

  const visibleCategories = useMemo(() => {
    const q = search.trim().toLowerCase()

    return BUILT_IN_TIPS.map(cat => {
      if (activeCategories.length > 0 && !activeCategories.includes(cat.category)) return null
      if (myNotesOnly && !(notes[cat.category]?.length > 0)) return null

      const myNotes = notes[cat.category] || []

      if (!q) return { ...cat, myNotes }

      const matchedTips = cat.tips.filter(
        t => t.title.toLowerCase().includes(q) || t.body.toLowerCase().includes(q)
      )
      const matchedNotes = myNotes.filter(
        n => n.title.toLowerCase().includes(q) || (n.body || '').toLowerCase().includes(q)
      )

      if (!matchedTips.length && !matchedNotes.length) return null
      return { ...cat, tips: matchedTips, myNotes: matchedNotes }
    }).filter(Boolean)
  }, [search, activeCategories, myNotesOnly, notes])

  // ── Export ──────────────────────────────────────────────────────────────────

  function handleExport() {
    const lines = ['My Cooking Tips & Notes', '=======================', `Exported ${todayISO()}`, '']

    BUILT_IN_TIPS.forEach(cat => {
      const catNotes = notes[cat.category] || []
      if (!catNotes.length) return
      lines.push(cat.category)
      lines.push('-'.repeat(cat.category.length))
      catNotes.forEach(n => {
        lines.push(`• ${n.title}`)
        if (n.body) lines.push(`  ${n.body}`)
        if (n.user_note) lines.push(`  Note: ${n.user_note}`)
      })
      lines.push('')
    })

    if (cookingNotes.length > 0) {
      lines.push('My Cooking Notes')
      lines.push('----------------')
      cookingNotes.forEach(n => {
        if (n.title) lines.push(`▸ ${n.title}`)
        lines.push(n.body)
        lines.push('')
      })
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `cooking-tips-${todayISO()}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  const totalMyNotes = Object.values(notes).reduce((s, arr) => s + (arr?.length || 0), 0)

  return (
    <>
      {/* ── Filter bar ── */}
      <Card label="Tips & Tricks" title="Filter">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input
            className="field-input"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search tips…"
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            <button
              className={`archetype-chip ${myNotesOnly ? 'active' : ''}`}
              onClick={() => setMyNotesOnly(v => !v)}
            >
              My notes only
            </button>
            <span style={{ width: 1, height: 20, background: 'rgba(0,0,0,0.12)', margin: '0 2px' }} />
            {ALL_CATEGORIES.map(cat => (
              <button
                key={cat}
                className={`archetype-chip ${activeCategories.includes(cat) ? 'active' : ''}`}
                onClick={() => toggleCategory(cat)}
              >
                {cat}
              </button>
            ))}
            {hasFilters && (
              <button className="ghost-btn" style={{ fontSize: '0.72rem', marginLeft: 'auto' }} onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* ── Export button ── */}
      {totalMyNotes > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
          <button className="tips-export-btn" onClick={handleExport}>
            ↓ Export my tips
          </button>
        </div>
      )}

      {/* ── Category cards ── */}
      {visibleCategories.length === 0 ? (
        <Card label="Tips & Tricks" title="">
          <p style={{ color: 'var(--stone)', fontStyle: 'italic', fontSize: '0.88rem' }}>
            No tips match your filters.
          </p>
        </Card>
      ) : (
        visibleCategories.map(cat => {
          const isExpanded  = expandedCats[cat.category]
          const shownTips   = isExpanded ? cat.tips : cat.tips.slice(0, TIPS_PER_CAT)
          const hiddenCount = cat.tips.length - TIPS_PER_CAT

          return (
            <Card key={cat.category} label="Tips & Tricks" title={cat.category}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>

                {/* Built-in tips (max 3 by default) */}
                {!myNotesOnly && shownTips.map(tip => (
                  <div
                    key={tip.title}
                    className="tip-accordion"
                    onClick={() => setOpenTip(openTip === tip.title ? null : tip.title)}
                  >
                    <div className="tip-accordion-header">
                      <span>{tip.title}</span>
                      <span className="tip-accordion-arrow">{openTip === tip.title ? '▲' : '▼'}</span>
                    </div>
                    {openTip === tip.title && (
                      <p className="tip-accordion-body">{tip.body}</p>
                    )}
                  </div>
                ))}

                {/* Show more / less for built-in tips */}
                {!myNotesOnly && hiddenCount > 0 && (
                  <button
                    className="cook-show-more"
                    onClick={() => setExpandedCats(prev => ({ ...prev, [cat.category]: !isExpanded }))}
                  >
                    {isExpanded ? 'Show less ▲' : `Show ${hiddenCount} more ▼`}
                  </button>
                )}

                {/* User notes */}
                {cat.myNotes.length > 0 && (
                  <div style={{ marginTop: cat.tips.length > 0 && !myNotesOnly ? 6 : 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {cat.myNotes.map(n => (
                      <UserNoteCard
                        key={n.id}
                        category={cat.category}
                        note={n}
                        onUpdate={updateNote}
                        onDelete={deleteNote}
                      />
                    ))}
                  </div>
                )}

                <AddNoteForm category={cat.category} onSave={addNote} />
              </div>
            </Card>
          )
        })
      )}

      {/* ── My Cooking Notes ── */}
      <div className="cooking-notes-card">
        <div className="cooking-notes-card__header">
          <span className="cooking-notes-card__title">My cooking notes</span>
          <button
            className="ghost-btn"
            style={{ fontSize: '0.78rem' }}
            onClick={() => setAddingCookNote(true)}
          >
            + Add note
          </button>
        </div>

        {addingCookNote && (
          <div className="cooking-note-form">
            <input
              className="field-input"
              value={cookNoteDraft.title}
              onChange={e => setCookNoteDraft(d => ({ ...d, title: e.target.value }))}
              placeholder="Note title (optional)"
              autoFocus
            />
            <textarea
              className="field-textarea"
              rows={4}
              value={cookNoteDraft.body}
              onChange={e => setCookNoteDraft(d => ({ ...d, body: e.target.value }))}
              placeholder="Write what you learned, what worked, what to improve…"
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className={`save-btn ${cookNoteDraft.body.trim() ? 'ready' : ''}`}
                onClick={addCookingNote}
                disabled={!cookNoteDraft.body.trim()}
              >
                Save note
              </button>
              <button className="ghost-btn" onClick={() => { setAddingCookNote(false); setCookNoteDraft({ title: '', body: '' }) }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {cookingNotes.length === 0 && !addingCookNote && (
          <p style={{ fontSize: '0.82rem', color: 'var(--stone)', fontStyle: 'italic' }}>
            Write what you have learned — techniques, mistakes, discoveries 🍳
          </p>
        )}

        {cookingNotes.map(note => (
          <CookingNoteCard key={note.id} note={note} onDelete={deleteCookingNote} />
        ))}
      </div>
    </>
  )
}
